// /opt/isp_erp/frontend/src/pages/warehouse/Movimientos.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  InputAdornment,
  Button,
  IconButton,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
  Stepper,
  Step,
  StepLabel
} from '@mui/material';
import {
  Search,
  Refresh,
  Visibility,
  Clear,
  Timeline,
  SwapHoriz,
  Assignment,
  Inventory,
  Home,
  Build,
  ReportProblem,
  ConfirmationNumber,
  LocalOffer,
  ArrowForward,
  CheckCircle,
  Pending,
  Warning
} from '@mui/icons-material';
import { warehouseAPI } from '../../api/warehouse.api';
import { cargarContextoBodega } from '../../utils/contextoBodega';
import CabeceraBodega from '../../components/warehouse/CabeceraBodega';

const Movimientos = () => {
  const [movimientos, setMovimientos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [tipoFilter, setTipoFilter] = useState('todos');
  const [dateRange, setDateRange] = useState({
    start: '',
    end: ''
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedMovimiento, setSelectedMovimiento] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [ctx, setCtx] = useState(null);
  const [ubicacion, setUbicacion] = useState('');

  useEffect(() => {
    cargarContextoBodega().then((c) => setCtx(c || { central: true, sucursales: [] }));
  }, []);

  useEffect(() => {
    if (!ctx) return;
    cargarMovimientos();
    if (ctx.central !== false) cargarEstadisticas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx, ubicacion]);

  const cargarMovimientos = async () => {
    setLoading(true);
    try {
      const response = await warehouseAPI.getMovimientos(ubicacion && ctx && ctx.central !== false ? { ubicacion } : undefined);
      setMovimientos(response.data || []);
    } catch (error) {
      console.error('Error cargando movimientos:', error);
    } finally {
      setLoading(false);
    }
  };

  const cargarEstadisticas = async () => {
    try {
      const response = await warehouseAPI.getEstadisticas();
      setStats(response.data);
    } catch (error) {
      console.error('Error cargando estadísticas:', error);
    }
  };

  const getTipoColor = (tipo) => {
    const colors = {
      DESPACHO_BODEGA: 'primary',
      INSTALACION: 'success',
      RETIRO_CAMPO: 'warning',
      RECEPCION_BODEGA: 'info',
      TRANSFERENCIA: 'default',
      RMA: 'error',
      REINGRESO: 'secondary'
    };
    return colors[tipo] || 'default';
  };

  const getTipoTexto = (tipo) => {
    const textos = {
      DESPACHO_BODEGA: 'Despacho a Técnico',
      INSTALACION: 'Instalación en Cliente',
      RETIRO_CAMPO: 'Retiro de Campo',
      RECEPCION_BODEGA: 'Recepción en Bodega',
      TRANSFERENCIA: 'Transferencia entre Técnicos',
      RMA: 'RMA / Baja Técnica',
      REINGRESO: 'Reingreso a Inventario'
    };
    return textos[tipo] || tipo;
  };

  const getEstadoColor = (estado) => {
    const colors = {
      'En Bodega': 'success',
      'En Tránsito': 'warning',
      'Instalado': 'info',
      'Retiro Pendiente': 'error',
      'En Sucursal': 'secondary',
      'RMA/Baja': 'default'
    };
    return colors[estado] || 'default';
  };

  const getIconoEstado = (estado) => {
    if (estado === 'En Bodega') return <Inventory fontSize="small" />;
    if (estado === 'En Tránsito') return <Pending fontSize="small" />;
    if (estado === 'Instalado') return <CheckCircle fontSize="small" />;
    if (estado === 'En Sucursal') return <Inventory fontSize="small" />;
    if (estado === 'Retiro Pendiente') return <Warning fontSize="small" />;
    return <Timeline fontSize="small" />;
  };

  const getIconoTipo = (tipo) => {
    switch(tipo) {
      case 'DESPACHO_BODEGA': return <Inventory fontSize="small" />;
      case 'INSTALACION': return <Home fontSize="small" />;
      case 'RETIRO_CAMPO': return <SwapHoriz fontSize="small" />;
      case 'RECEPCION_BODEGA': return <Assignment fontSize="small" />;
      default: return <Timeline fontSize="small" />;
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const verDetalle = (movimiento) => {
    setSelectedMovimiento(movimiento);
    setDetailOpen(true);
  };

  const filteredMovimientos = movimientos.filter(mov => {
    const matchSearch = mov.equipo?.sn?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       mov.origen_nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       mov.destino_nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       (mov.ticket_number && mov.ticket_number.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchTipo = tipoFilter === 'todos' || mov.tipo_movimiento === tipoFilter;

    let matchDate = true;
    if (dateRange.start) {
      const movDate = new Date(mov.fecha_movimiento);
      const startDate = new Date(dateRange.start);
      if (movDate < startDate) matchDate = false;
    }
    if (dateRange.end && matchDate) {
      const movDate = new Date(mov.fecha_movimiento);
      const endDate = new Date(dateRange.end);
      endDate.setHours(23, 59, 59);
      if (movDate > endDate) matchDate = false;
    }

    return matchSearch && matchTipo && matchDate;
  });

  const paginatedMovimientos = filteredMovimientos.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const limpiarFiltros = () => {
    setSearchTerm('');
    setTipoFilter('todos');
    setDateRange({ start: '', end: '' });
  };

  const renderMateriales = (materiales) => {
    if (!materiales || materiales.length === 0) return null;
    return (
      <Box sx={{ mt: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          <Build sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
          Materiales Utilizados:
        </Typography>
        <List dense sx={{ bgcolor: '#f5f5f5', borderRadius: 1 }}>
          {materiales.map((mat, idx) => (
            <ListItem key={idx} sx={{ py: 0 }}>
              <ListItemText 
                primary={`${mat.nombre} x${mat.cantidad}`}
                secondary={mat.observaciones}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
          ))}
        </List>
      </Box>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <CabeceraBodega titulo="Historial de Movimientos" subtitulo="Todo lo que se ha movido: despachos, instalaciones, retiros y recepciones." />

      {/* Estadísticas rápidas */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={2.4}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4">{stats.total_equipos || 0}</Typography>
              <Typography variant="body2" color="text.secondary">Total Equipos</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={2.4}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#e8f5e9' }}>
              <Typography variant="h4" color="success.main">{stats.en_bodega || 0}</Typography>
              <Typography variant="body2">En Bodega</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={2.4}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#fff3e0' }}>
              <Typography variant="h4" color="warning.main">{stats.en_transito || 0}</Typography>
              <Typography variant="body2">En Tránsito</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={2.4}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#e3f2fd' }}>
              <Typography variant="h4" color="info.main">{stats.instalados || 0}</Typography>
              <Typography variant="body2">Instalados</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={2.4}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#ffebee' }}>
              <Typography variant="h4" color="error.main">{stats.retiro_pendiente || 0}</Typography>
              <Typography variant="body2">Retiro Pendiente</Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          {ctx && ctx.central === false && (
            <Alert severity="info" sx={{ mb: 2 }}>Estás viendo solo los movimientos de tu sucursal y de tus técnicos.</Alert>
          )}
          {ctx && ctx.central !== false && ctx.sucursales && ctx.sucursales.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 260, mb: 2 }}>
              <InputLabel>Ubicación</InputLabel>
              <Select value={ubicacion} label="Ubicación" onChange={(e) => { setUbicacion(e.target.value); setPage(0); }}>
                <MenuItem value="">Todas</MenuItem>
                <MenuItem value="central">Bodega central</MenuItem>
                {ctx.sucursales.map((s) => (<MenuItem key={s.id} value={String(s.id)}>{s.nombre}</MenuItem>))}
              </Select>
            </FormControl>
          )}
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Buscar por SN, origen, destino o ticket..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Tipo de Movimiento</InputLabel>
                <Select
                  value={tipoFilter}
                  onChange={(e) => setTipoFilter(e.target.value)}
                  label="Tipo de Movimiento"
                >
                  <MenuItem value="todos">Todos</MenuItem>
                  <MenuItem value="DESPACHO_BODEGA">📦 Despacho a Técnico</MenuItem>
                  <MenuItem value="INSTALACION">🏠 Instalación en Cliente</MenuItem>
                  <MenuItem value="RETIRO_CAMPO">⚠️ Retiro de Campo</MenuItem>
                  <MenuItem value="RECEPCION_BODEGA">📥 Recepción en Bodega</MenuItem>
                  <MenuItem value="TRANSFERENCIA">🔄 Transferencia</MenuItem>
                  <MenuItem value="RMA">🔧 RMA / Baja</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Desde"
                value={dateRange.start}
                onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Hasta"
                value={dateRange.end}
                onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={1}>
              <Button
                fullWidth
                variant="outlined"
                onClick={limpiarFiltros}
                startIcon={<Clear />}
              >
                Limpiar
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell>Fecha/Hora</TableCell>
              <TableCell>Movimiento</TableCell>
              <TableCell>Estado Anterior → Nuevo</TableCell>
              <TableCell>Origen → Destino</TableCell>
              <TableCell>Equipo</TableCell>
              <TableCell>Info Adicional</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                  <CircularProgress size={40} />
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Cargando historial...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : paginatedMovimientos.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                  <Timeline sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                  <Typography variant="body1" color="text.secondary">
                    No hay movimientos registrados
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedMovimientos.map((mov) => (
                <TableRow key={mov.id} hover>
                  <TableCell>{formatDate(mov.fecha_movimiento)}</TableCell>
                  <TableCell>
                    <Chip
                      icon={getIconoTipo(mov.tipo_movimiento)}
                      label={getTipoTexto(mov.tipo_movimiento)}
                      color={getTipoColor(mov.tipo_movimiento)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {mov.estado_origen && (
                        <Chip
                          icon={getIconoEstado(mov.estado_origen)}
                          label={mov.estado_origen}
                          size="small"
                          variant="outlined"
                          color={getEstadoColor(mov.estado_origen)}
                        />
                      )}
                      {mov.estado_origen && mov.estado_destino && (
                        <ArrowForward fontSize="small" color="action" />
                      )}
                      {mov.estado_destino && (
                        <Chip
                          icon={getIconoEstado(mov.estado_destino)}
                          label={mov.estado_destino}
                          size="small"
                          color={getEstadoColor(mov.estado_destino)}
                        />
                      )}
                      {!mov.estado_origen && !mov.estado_destino && (
                        <Typography variant="caption" color="text.secondary">-</Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2">{mov.origen_nombre}</Typography>
                      <ArrowForward fontSize="small" color="action" />
                      <Typography variant="body2" fontWeight="medium">{mov.destino_nombre}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={`SN: ${mov.equipo?.sn || mov.equipment_id || '-'}`}>
                      <Typography variant="body2" fontWeight="bold" noWrap sx={{ maxWidth: 150 }}>
                        {mov.equipo?.sn || mov.equipment_id || '-'}
                      </Typography>
                    </Tooltip>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {mov.equipo?.modelo || ''}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {mov.ticket_number && (
                        <Tooltip title={`Ticket #${mov.ticket_number}`}>
                          <Chip
                            icon={<ConfirmationNumber fontSize="small" />}
                            label={mov.ticket_number}
                            size="small"
                            variant="outlined"
                          />
                        </Tooltip>
                      )}
                      {mov.tipo_movimiento === 'INSTALACION' && mov.materiales_utilizados?.length > 0 && (
                        <Tooltip title={`${mov.materiales_utilizados.length} material(es) utilizado(s)`}>
                          <Chip
                            icon={<Build fontSize="small" />}
                            label={`${mov.materiales_utilizados.length} mat.`}
                            size="small"
                            color="info"
                          />
                        </Tooltip>
                      )}
                      {mov.tipo_movimiento === 'RETIRO_CAMPO' && mov.motivo_retiro && (
                        <Tooltip title={mov.motivo_retiro}>
                          <Chip
                            icon={<ReportProblem fontSize="small" />}
                            label="Motivo"
                            size="small"
                            color="warning"
                          />
                        </Tooltip>
                      )}
                      {mov.customer_name && (
                        <Tooltip title={`Cliente: ${mov.customer_name}`}>
                          <Chip
                            icon={<LocalOffer fontSize="small" />}
                            label={mov.customer_name.length > 15 ? mov.customer_name.substring(0, 15) + '...' : mov.customer_name}
                            size="small"
                            variant="outlined"
                          />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Ver detalle completo">
                      <IconButton size="small" onClick={() => verDetalle(mov)}>
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredMovimientos.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </TableContainer>

      {/* Dialog de Detalle Mejorado */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Detalle del Movimiento
          {selectedMovimiento?.tipo_movimiento && (
            <Chip
              icon={getIconoTipo(selectedMovimiento.tipo_movimiento)}
              label={getTipoTexto(selectedMovimiento.tipo_movimiento)}
              color={getTipoColor(selectedMovimiento.tipo_movimiento)}
              size="small"
              sx={{ ml: 2 }}
            />
          )}
        </DialogTitle>
        <DialogContent>
          {selectedMovimiento && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Fecha:</Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {formatDate(selectedMovimiento.fecha_movimiento)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Usuario:</Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {selectedMovimiento.usuario_nombre || 'Sistema'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body2" color="text.secondary">Cambio de Estado:</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                    {selectedMovimiento.estado_origen && (
                      <Chip
                        icon={getIconoEstado(selectedMovimiento.estado_origen)}
                        label={selectedMovimiento.estado_origen}
                        color={getEstadoColor(selectedMovimiento.estado_origen)}
                      />
                    )}
                    {selectedMovimiento.estado_origen && selectedMovimiento.estado_destino && (
                      <ArrowForward />
                    )}
                    {selectedMovimiento.estado_destino && (
                      <Chip
                        icon={getIconoEstado(selectedMovimiento.estado_destino)}
                        label={selectedMovimiento.estado_destino}
                        color={getEstadoColor(selectedMovimiento.estado_destino)}
                      />
                    )}
                  </Box>
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Trayecto:</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <Typography variant="body1">{selectedMovimiento.origen_nombre}</Typography>
                    <ArrowForward />
                    <Typography variant="body1" fontWeight="bold">{selectedMovimiento.destino_nombre}</Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body2" color="text.secondary">Equipo:</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {selectedMovimiento.equipo?.sn || selectedMovimiento.equipment_id || 'No especificado'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Modelo: {selectedMovimiento.equipo?.modelo || 'No especificado'}
                  </Typography>
                </Grid>
                
                {selectedMovimiento.customer_name && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">Cliente:</Typography>
                    <Typography variant="body1">{selectedMovimiento.customer_name}</Typography>
                  </Grid>
                )}
                
                {selectedMovimiento.ticket_number && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      <ConfirmationNumber sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                      Ticket Asociado:
                    </Typography>
                    <Typography variant="body1" color="primary">
                      #{selectedMovimiento.ticket_number}
                    </Typography>
                  </Grid>
                )}
                
                {selectedMovimiento.motivo_retiro && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      <ReportProblem sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                      Motivo de Retiro:
                    </Typography>
                    <Paper sx={{ p: 2, bgcolor: '#fff3e0', mt: 0.5 }}>
                      <Typography variant="body2">{selectedMovimiento.motivo_retiro}</Typography>
                    </Paper>
                  </Grid>
                )}
                
                {selectedMovimiento.materiales_utilizados && selectedMovimiento.materiales_utilizados.length > 0 && (
                  <Grid item xs={12}>
                    {renderMateriales(selectedMovimiento.materiales_utilizados)}
                  </Grid>
                )}
                
                {selectedMovimiento.observaciones && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">Observaciones:</Typography>
                    <Paper sx={{ p: 2, bgcolor: '#f5f5f5', mt: 0.5 }}>
                      <Typography variant="body2">{selectedMovimiento.observaciones}</Typography>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Movimientos;
